import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PersonOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
  ShieldOutlined,
  TranslateOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Logo from '../assets/images/FastLine logo-01 1.png';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, toggleLanguage, dir, isRtl } = useLanguage();

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userName.trim() || !password.trim()) {
      setErrorMsg(t('login.fillRequired'));
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const result = await login(userName, password);
      if (result.success) {
        navigate('/home');
      } else {
        setErrorMsg(result.message || t('login.loginFailed'));
      }
    } catch (err) {
      setErrorMsg(t('login.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F1F5F9',
        backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        p: 2,
        direction: dir,
        position: 'relative',
      }}
    >
      {/* Top Language Switcher */}
      <Box sx={{ position: 'absolute', top: 16, right: isRtl ? 16 : 'auto', left: isRtl ? 'auto' : 16 }}>
        <Button
          onClick={toggleLanguage}
          size="small"
          startIcon={<TranslateOutlined sx={{ fontSize: 16 }} />}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            fontWeight: 700,
            fontSize: '0.78rem',
            border: '1px solid #E2E8F0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            color: 'text.primary',
          }}
        >
          {language === 'ar' ? 'English' : 'العربية'}
        </Button>
      </Box>

      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.03)',
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
          direction: dir,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Brand Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={Logo}
              alt="FastLine"
              sx={{
                height: 48,
                maxWidth: 180,
                objectFit: 'contain',
                mb: 1.5,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E255E', fontSize: '1.15rem' }}>
              {t('appName')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.76rem' }}>
              {t('login.subtitle')}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '8px', fontSize: '0.78rem', textAlign: isRtl ? 'right' : 'left' }}>
              {errorMsg}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              <Typography variant="caption" sx={{ color: '#475569', mb: 0.5, fontWeight: 700, display: 'block', textAlign: isRtl ? 'right' : 'left' }}>
                {t('login.usernameLabel')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder={t('login.usernamePlaceholder')}
                inputProps={{ style: { textAlign: isRtl ? 'right' : 'left', direction: dir } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutline sx={{ color: '#94A3B8', fontSize: 19 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              <Typography variant="caption" sx={{ color: '#475569', mb: 0.5, fontWeight: 700, display: 'block', textAlign: isRtl ? 'right' : 'left' }}>
                {t('login.passwordLabel')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                inputProps={{ style: { textAlign: isRtl ? 'right' : 'left', direction: dir } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#94A3B8', fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: '#94A3B8' }}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 1,
                py: 1.1,
                fontSize: '0.86rem',
                fontWeight: 700,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : t('login.submitBtn')}
            </Button>
          </Box>

          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #F1F5F9', textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: '#94A3B8' }}>
              <ShieldOutlined sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {t('login.secureSystem')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
